// Implements: F-RPT-006, F-UIX-019
/**
 * Mockup v3 P9 Estado: the latency and errors of one organization per model and step (v3
 * `:1916-1919`). Pure presentation.
 *
 * The rate at or above the 3 % objective is flagged with colour AND words (the sr-only
 * " (por encima del 3 % objetivo)"), never colour alone. Durations are seconds with a decimal
 * comma; a step role reads in Spanish, an unknown one as it came. The help line says what a
 * failed step counts.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { LatencyRowView } from "../../../types/reports";
import { isAboveObjective, rateText, secondsText } from "../model/latencyFormat";
import { stepRoleText } from "./flowLabels";
import styles from "./Latency.module.css";

export interface LatencyTableProps {
  readonly rows: readonly LatencyRowView[];
}

export function LatencyTable({ rows }: LatencyTableProps): ReactElement {
  const { t } = useTranslation();
  const seconds = (ms: number): string => t("reports.latency.seconds", { seconds: secondsText(ms) });
  return (
    <>
      <div className={styles.wrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">{t("reports.latency.column.model")}</th>
              <th scope="col">{t("reports.latency.column.step")}</th>
              <th scope="col" className={styles.number}>
                {t("reports.latency.column.calls")}
              </th>
              <th scope="col" className={styles.number}>
                {t("reports.latency.column.failures")}
              </th>
              <th scope="col" className={styles.number}>
                {t("reports.latency.column.rate")}
              </th>
              <th scope="col" className={styles.number}>
                {t("reports.latency.column.pFifty")}
              </th>
              <th scope="col" className={styles.number}>
                {t("reports.latency.column.pNinetyFive")}
              </th>
              <th scope="col" className={styles.number}>
                {t("reports.latency.column.max")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const bad = isAboveObjective(row.error_rate);
              return (
                <tr key={`${row.model}:${row.step}`}>
                  <th scope="row">{row.model}</th>
                  <td>
                    <span className={styles.badge}>{stepRoleText(row.step)}</span>
                  </td>
                  <td className={styles.number}>{row.calls}</td>
                  <td className={styles.number}>{row.failures}</td>
                  <td className={[styles.number, bad ? styles.rateBad : undefined].filter(Boolean).join(" ")}>
                    {t("reports.latency.rate", { rate: rateText(row.error_rate) })}
                    {bad ? <span className={styles.srOnly}>{t("reports.latency.rateBad")}</span> : null}
                  </td>
                  <td className={styles.number}>{seconds(row.p50_ms)}</td>
                  <td className={styles.number}>{seconds(row.p95_ms)}</td>
                  <td className={styles.number}>{seconds(row.max_ms)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={styles.help}>{t("reports.latency.help")}</p>
    </>
  );
}
